// Shared formatting helpers for the dashboard widgets. Anything that
// renders a number should pass through one of these so we keep the
// visual language consistent (compact thousands / millions, EUR signs,
// percentage rounding, etc).

export function formatNumber(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(1) + 'K';
  return String(Math.round(v));
}

export function formatCurrency(n) {
  const v = Number(n) || 0;
  if (v >= 1000) return '€' + formatNumber(v);
  if (v >= 10) return '€' + Math.round(v);
  return '€' + v.toFixed(2);
}

export function formatPercent(n, decimals = 1) {
  return (Number(n) || 0).toFixed(decimals) + '%';
}

// Currency for amounts that can be a fraction of a cent: one router call, a per-call
// average, a quiet day's router spend. formatCurrency rounds those to €0.00, which reads
// as "free"; below €1 this keeps three significant digits (€0.075, €0.00013), with at
// least two decimals so it still reads as money.
export function formatCost(n) {
  const v = Number(n) || 0;
  if (v === 0) return '€0';
  if (Math.abs(v) >= 1) return formatCurrency(v);
  const decimals = Math.min(8, Math.max(2, 2 - Math.floor(Math.log10(Math.abs(v)))));
  const digits = Math.abs(v).toFixed(decimals).replace(/(\.\d{2}\d*?)0+$/, '$1');
  return (v < 0 ? '-€' : '€') + digits;
}

// Shared 12-colour SERIES palette, used by every chart that auto-assigns colours
// (per-model lines, per-source stacked areas, per-action stacked bars…).
//
// The previous twelve were drawn from at least three unrelated palettes — flat-UI
// (#2ecc71, #e74c3c, #9b59b6), a Tailwind-ish set (#667eea, #f093fb) and Material
// (#ffa726) — so no chart colour related to any other colour in the product, and two
// of them happened to equal the old error and success colours, which made a neutral
// fifth series read as a failure.
//
// This sequence starts on the two brand colours, so a one- or two-series chart is on
// brand, then walks the hue circle for separation. Adjacent pairs stay distinguishable
// under deuteranopia (the worst case is 8/11 blue-vs-cyan, which are far apart in the
// order).
//
// CSS custom properties, so the palette follows the theme: the light values are the
// original hexes, tuned for a white card; the dark values (styles/tokens.css) are the
// same hues lifted to stay legible on a dark one. This file used to insist on literal
// hexes because "an attribute is the one place a custom property is least reliable".
// Measured 2026-09-26 in Chromium, Firefox and WebKit: `fill="var(--x)"` and
// `stroke="var(--x)"` presentation attributes all resolve (_ui_harness/svgvar_check.py),
// and TimeChart had been relying on exactly that for its axis text all along.
export const PALETTE = [
  'var(--series-1)',   //  1  indigo — brand primary        (#3730A3 light)
  'var(--series-2)',   //  2  teal                          (#0E9594)
  'var(--series-3)',   //  3  burnt orange                  (#C2410C)
  'var(--series-4)',   //  4  violet — brand secondary      (#7C3AED)
  'var(--series-5)',   //  5  red                           (#B42318)
  'var(--series-6)',   //  6  green                         (#12855B)
  'var(--series-7)',   //  7  ochre, brand yellow darkened  (#9E7700)
  'var(--series-8)',   //  8  blue                          (#2563EB)
  'var(--series-9)',   //  9  magenta                       (#A21CAF)
  'var(--series-10)',  // 10  slate                         (#475569)
  'var(--series-11)',  // 11  cyan                          (#0891B2)
  'var(--series-12)',  // 12  lime                          (#65A30D)
];

// Percentile series — the ONE place a ramp is used instead of PALETTE.
//
// PALETTE is categorical: it maximises separation because its series are unordered
// (model A vs model B). Percentiles are the opposite — p5 → p99 is a single ordered
// quantity, and seven unrelated hues make a latency chart read as seven independent
// metrics that happen to share an axis. The reader's first question is "which line is
// the slow one", and the colour should answer it without the legend.
//
// So this walks COOL → WARM in one direction (fast → slow), which encodes the order,
// while still changing hue between neighbours — a pure lightness ramp would be
// monotonic but makes adjacent pairs (p50 vs p75) indistinguishable at 2px stroke
// width, and adjacent pairs are exactly what a percentile chart is compared against.
//
// Four of the seven are PALETTE entries, and the other three are intermediate steps on
// the same walk, so this stays inside the product's colour language rather than
// introducing a second one.
export const PERCENTILE_KEYS = ['p5', 'p10', 'p20', 'p50', 'p75', 'p90', 'p99'];
export const PERCENTILE_COLORS = [
  '#2DD4BF',    // p5   bright teal — the fast tail, and the least-read line
  PALETTE[1],   // p10  teal
  PALETTE[10],  // p20  cyan
  PALETTE[7],   // p50  blue — the median, deliberately the first saturated step
  '#5B5BD6',    // p75  indigo; mid-tone so it holds 3:1 on the white AND the dark card
  PALETTE[3],   // p90  violet
  PALETTE[8],   // p99  magenta — the slow tail, the darkest and most prominent
];

// Shared Recharts chrome, so every chart's tooltip, grid and axes follow the theme
// instead of each file hard-coding a dark tooltip and a grey grid.
export const CHART_TOOLTIP = {
  contentStyle: {
    background: 'var(--surface-raised)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    boxShadow: 'var(--shadow-md)',
    color: 'var(--text)',
    fontSize: 'var(--fs-sm)',
    padding: '8px 10px',
  },
  itemStyle: { color: 'var(--text-2)', padding: 0 },
  labelStyle: { color: 'var(--text)', fontWeight: 600, marginBottom: 4 },
  cursor: { fill: 'var(--chart-track)' },
};

export const CHART_AXIS = {
  tick: { fontSize: 10.5, fill: 'var(--chart-axis)' },
  tickLine: false,
  axisLine: { stroke: 'var(--chart-grid)' },
};

export const CHART_GRID = {
  strokeDasharray: '3 3',
  stroke: 'var(--chart-grid)',
  vertical: false,
};

// Bytes, for the platform charts (memory, network, storage). Binary units, because
// that is what CloudWatch and the container limits are expressed in.
export function formatBytes(n, decimals = 1) {
  const v = Number(n) || 0;
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let i = 0;
  let x = Math.abs(v);
  while (x >= 1024 && i < units.length - 1) { x /= 1024; i += 1; }
  return `${v < 0 ? '-' : ''}${i === 0 ? Math.round(x) : x.toFixed(decimals)} ${units[i]}`;
}

// Milliseconds, for the latency charts. Switches to seconds at 1s because a
// four-or-five-digit millisecond count is unreadable as a duration, and an LLM turn is
// routinely tens of seconds — an axis reading "45,000" invites being read as a count of
// something. One decimal below 10s keeps sub-second resolution visible where it matters.
export function formatMs(n) {
  const v = Number(n) || 0;
  if (v < 1000) return Math.round(v) + 'ms';
  const s = v / 1000;
  return (s < 10 ? s.toFixed(1) : Math.round(s)) + 's';
}

// Domain-specific palettes, drawn FROM the series palette rather than invented, so a
// named category and an auto-assigned one cannot end up different shades of the same
// idea on the same screen.
export const SOURCE_COLORS = {
  manual:  PALETTE[0],   // indigo
  coupon:  PALETTE[6],   // ochre
  stripe:  PALETTE[5],   // green
};

export const CHANNEL_COLORS = {
  telegram: PALETTE[1],  // teal
};

// Each container-lifecycle action gets a visually distinct color so the
// stacked time-series lines stay readable when they overlap.
//   create  → green   · start   → blue   · stop  → orange
//   restart → purple  · delete  → red    · retention_wipe → slate
// Previously create/start were both green-family and delete/retention_wipe
// were both red-family, which made the legend ambiguous.
export const ACTION_COLORS = {
  create:          PALETTE[5],   // green
  start:           PALETTE[7],   // blue
  stop:            PALETTE[6],   // ochre
  restart:         PALETTE[3],   // violet
  delete:          PALETTE[4],   // red
  retention_wipe:  PALETTE[9],   // slate
  unknown:         'var(--ink-4)',
};
